import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { AppDialog } from '../../../components/AppDialog/AppDialog';
import { useSaveFlow } from '../../../components/confirm';
import { formatCombo } from '../../../components/hotkeys';
import { useNotify } from '../../../components/notifications';
import { useAuthStore } from '../../../auth/store';
import { useNavPermission } from '../../../hooks/useNavPermission';
import { confirmDiscardIfUnsaved, markUserEdit, resetUnsavedTracking } from '../../../hooks/useUnsavedGuard';
import { fmtLocalDateTime } from '../../../utils/datetime';
import {
  DAILY_QC_FORM, DAILY_QC_NAV_ID, createEntry, downloadAttachment, errorText, evaluateEntry, fetchAnalysts, fetchEntry, fetchHistory,
  fetchLogbook, isEmpty, isValidationError, ruleText, updateEntry, uploadFile,
  type DailyQcAnalyst, type DailyQcEntry, type DailyQcEntryInput, type DailyQcEvaluation, type DailyQcField,
  type DailyQcLogbookDetail,
} from './dailyQcApi';
import { DailyQcSparkline } from './DailyQcTrend';
import { STATUS_PILL, seriesFor, type TrendPoint } from './dailyQcFormat';

/** Sparklines look back this far (a history preset) — enough for the last 15 results of a monthly logbook. */
const SPARK_LOOKBACK = '2Y';
const SPARK_POINTS = 15;
/** Debounce of the live pass/fail evaluation while typing. */
const EVAL_DELAY_MS = 250;
/** Popup width (prototype `.modal.wide`). */
const DIALOG_WIDTH = 880;

interface Props {
  templateId: number;
  /** null = New entry. */
  oid: string | null;
  /** After a successful save (the history reloads). */
  onSaved: () => void;
  onClose: () => void;
}

/**
 * The entry popup (owner request 2026-09-28: "same like the prototype" — New / Edit open
 * as a popup over the history, as in ALPACA_Daily_QC_Data_Entry_v1.html, instead of the
 * full-page detail of standing rule 12). One control per configured field in a 3-column
 * grid, calculated fields filled and every pass rule evaluated LIVE by the API as you
 * type (per-field Pass/Fail badge + overall status), a sparkline of the last 15 results
 * under each numeric field, Remark required when a check fails, Tested By.
 * Buttons: Cancel · Save & New (New only, keeps the date) · Save (saves and closes).
 */
export default function DailyQcEntryDialog({ templateId, oid, onSaved, onClose }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const perm = useNavPermission(DAILY_QC_NAV_ID);
  const me = useAuthStore((s) => s.user);

  const [detail, setDetail] = useState<DailyQcLogbookDetail | null>(null);
  const [analysts, setAnalysts] = useState<DailyQcAnalyst[]>([]);
  const [past, setPast] = useState<DailyQcEntry[]>([]);
  const [entry, setEntry] = useState<DailyQcEntry | null>(null);
  const [values, setValues] = useState<Record<string, string | null>>({});
  const [remark, setRemark] = useState('');
  const [testedBy, setTestedBy] = useState('');
  // The Attachment field's file: its dbo.FileData id (the field's own value holds the file name).
  const [attachOid, setAttachOid] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [ev, setEv] = useState<DailyQcEvaluation | null>(null);
  const [showMissing, setShowMissing] = useState(false);
  const [ready, setReady] = useState(false);
  const remarkRef = useRef<HTMLTextAreaElement>(null);
  const evalSeq = useRef(0);

  const fields = useMemo(() => detail?.fields ?? [], [detail]);
  // G5: only the user who created an entry may edit it (the API enforces the same rule).
  const notCreator = !!oid && entry?.canEdit === false;
  const readOnly = !perm.canWrite || (!oid && !(detail?.isActive ?? false)) || notCreator;

  const reloadPast = useCallback(async () => {
    try { setPast((await fetchHistory(templateId, { period: SPARK_LOOKBACK })).entries); }
    catch (e) { notify.error(errorText(e, 'Failed to load the recent results.')); }
  }, [templateId, notify]);

  // Logbook, Tested By choices and the recent results.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [d, a] = await Promise.all([fetchLogbook(templateId), fetchAnalysts()]);
        if (!live) return;
        setDetail(d); setAnalysts(a);
      } catch (e) {
        notify.error(errorText(e, 'Failed to open the logbook.'));
      }
    })();
    void reloadPast();
    return () => { live = false; };
  }, [templateId, notify, reloadPast]);

  /** Blank form for a New entry; `keepDate` carries the date over on Save & New. */
  const startNew = useCallback((keepDate?: string | null) => {
    if (!detail) return;
    const v: Record<string, string | null> = { ...detail.defaults };
    if (keepDate) v['Date'] = keepDate;
    setEntry(null); setValues(v); setRemark(''); setShowMissing(false); setAttachOid(null);
    setTestedBy(me?.id && analysts.some((a) => a.oid === me.id) ? me.id : '');
    setReady(true);
  }, [detail, analysts, me]);

  // Load the record (Edit) or the defaults (New).
  useEffect(() => {
    if (!detail) return;
    if (!oid) { startNew(); return; }
    let live = true;
    fetchEntry(oid)
      .then((e) => {
        if (!live) return;
        setEntry(e); setValues({ ...e.values }); setRemark(e.remark ?? ''); setTestedBy(e.testedByOid ?? ''); setAttachOid(e.attachOid ?? null);
        setShowMissing(false); setReady(true);
      })
      .catch((e) => notify.error(errorText(e, 'Failed to open the entry.')));
    return () => { live = false; };
  }, [oid, detail, startNew, notify]);

  const input = useCallback((): DailyQcEntryInput => ({
    values, remark: remark.trim() || null, testedBy: testedBy || null, attachOid,
  }), [values, remark, testedBy, attachOid]);

  /** Attach a file (G5 DailyQC.Attach): upload it, keep its id, show its name in the field. */
  const attach = async (f: DailyQcField, file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadFile(file);
      setAttachOid(r.oid);
      setValue(f.name, r.fileName);
    } catch (e) {
      notify.error(errorText(e, 'The file could not be uploaded.'));
    } finally {
      setUploading(false);
    }
  };

  // Live calculation + pass/fail from the API (debounced; the latest answer wins).
  useEffect(() => {
    if (!ready) return;
    const seq = ++evalSeq.current;
    const t = setTimeout(() => {
      evaluateEntry(templateId, { values, remark: null, testedBy: null })
        .then((r) => { if (seq === evalSeq.current) setEv(r); })
        .catch((e) => notify.error(errorText(e, 'Failed to evaluate the entry.')));
    }, EVAL_DELAY_MS);
    return () => clearTimeout(t);
  }, [values, ready, templateId, notify]);

  const setValue = (name: string, v: string | null) => setValues((prev) => ({ ...prev, [name]: v }));

  /** Yes / No toggle; clicking the pressed one again clears it (prototype). */
  const toggle = (f: DailyQcField, v: '1' | '0', e: ReactMouseEvent<HTMLButtonElement>) => {
    markUserEdit(e.currentTarget);   // a button is not a native input — tell the unsaved guard
    setValue(f.name, values[f.name] === v ? null : v);
  };

  const doSave = useCallback(async (andNew: boolean) => {
    if (!detail || readOnly) return;
    const body = input();
    let fresh: DailyQcEvaluation;
    try { fresh = await evaluateEntry(templateId, { values: body.values, remark: null, testedBy: null }); }
    catch (e) { notify.error(errorText(e, 'Failed to evaluate the entry.')); return; }
    setEv(fresh);
    if (fresh.missing.length > 0) {
      setShowMissing(true);
      notify.warning(`Complete required fields: ${fresh.missing.join(', ')}.`);
      return;
    }
    if (fresh.status === 'Fail' && !body.remark) {
      setShowMissing(true);
      notify.warning('A check failed. Describe the corrective action in Remark.');
      remarkRef.current?.focus();
      return;
    }
    const keepDate = values['Date'] ?? null;
    await runSave({
      formName: DAILY_QC_FORM,
      auditAction: oid ? 'edit' : 'create',
      save: async () => {
        if (oid) await updateEntry(oid, body);
        else await createEntry(templateId, body);
      },
      // A refusal (400 — e.g. G5's "TimeIn should be less than TimeOut.") is a validation
      // Warning like every form's; only a real failure is the red Error popup.
      onError: (e) => (isValidationError(e) ? notify.warning(errorText(e, 'Check the entry.')) : notify.error(errorText(e, 'Save failed.'))),
      onSaved: () => {
        onSaved();
        if (andNew) { void reloadPast(); startNew(keepDate); }
        else onClose();
      },
    });
  }, [detail, readOnly, input, templateId, notify, values, runSave, oid, onSaved, reloadPast, startNew, onClose]);

  /** Cancel / ✕ / Esc — warn first when something was typed (standing rule 15). */
  const cancel = useCallback(() => {
    void confirmDiscardIfUnsaved().then((ok) => { if (ok) { resetUnsavedTracking(); onClose(); } });
  }, [onClose]);

  const logbook = detail?.logbook;
  const status = ev?.status ?? entry?.status ?? 'Incomplete';
  const failing = status === 'Fail';
  const title = `${oid ? 'Edit Entry' : 'New Entry'} — ${logbook?.name ?? 'Daily QC'}`;
  // The template's own captions (G5: Analyst captioned "ArchivedBy", Remark captioned "Content").
  const testedByLabel = detail?.labels?.testedBy ?? 'Tested By';
  const remarkLabel = detail?.labels?.remark ?? 'Remark';

  const sparkFor = (f: DailyQcField) => {
    const pts: TrendPoint[] = seriesFor(f, past.filter((e) => e.oid !== oid)).slice(-SPARK_POINTS);
    const raw = f.calc ? ev?.calc[f.name] : values[f.name];
    const v = Number.parseFloat(raw ?? '');
    if (Number.isFinite(v)) pts.push({ d: '', v, ok: ev?.accept[f.name] ?? null, current: true });
    return <DailyQcSparkline points={pts} limits={detail?.limits[f.name] ?? []} />;
  };

  /** Choices plus the stored value when it is no longer offered (an old entry keeps showing it). */
  const withCurrent = (opts: string[], v: string) => (v && !opts.includes(v) ? [v, ...opts] : opts);

  const control = (f: DailyQcField) => {
    const id = `dqc_${f.name}`;
    const v = values[f.name] ?? '';
    const missing = showMissing && f.required && isEmpty(values[f.name]);
    const cls = `dqc-in${missing ? ' dqc-missing' : ''}`;
    if (f.calc) return <div className="dqc-in dqc-calc" id={id}>{ev?.calc[f.name] ?? values[f.name] ?? ''}</div>;
    switch (f.type) {
      case 'Date':
        return <input id={id} className={cls} type="date" value={v} disabled={readOnly} onChange={(e) => setValue(f.name, e.target.value)} />;
      case 'Time':
        return <input id={id} className={cls} type="time" value={v} disabled={readOnly} onChange={(e) => setValue(f.name, e.target.value)} />;
      case 'DateTime':
        return <input id={id} className={cls} type="datetime-local" value={v} disabled={readOnly} onChange={(e) => setValue(f.name, e.target.value)} />;
      case 'Numeric':
        return <input id={id} className={`${cls} dqc-num`} type="number" step="any" value={v} disabled={readOnly}
          onChange={(e) => setValue(f.name, e.target.value)} />;
      case 'Checkbox':
        return (
          <div id={id} className={`dqc-yn${missing ? ' dqc-missing' : ''}`} role="group" aria-label={f.caption}>
            <button type="button" className={`yes${v === '1' ? ' on' : ''}`} disabled={readOnly} aria-pressed={v === '1'}
              onClick={(e) => toggle(f, '1', e)}>Yes</button>
            <button type="button" className={`no${v === '0' ? ' on' : ''}`} disabled={readOnly} aria-pressed={v === '0'}
              onClick={(e) => toggle(f, '0', e)}>No</button>
          </div>
        );
      case 'ComboBox':
      case 'Lookup':   // choices = values recorded in the source logbook (G5 cross-logbook pick)
        return (
          <select id={id} className={cls} value={v} disabled={readOnly} onChange={(e) => setValue(f.name, e.target.value || null)}>
            <option value="" />
            {withCurrent(f.options, v).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      case 'Employee':  // G5 stores the employee's name as text (DestroyedBy / RetiredBy / CalibratedBy)
        return (
          <select id={id} className={cls} value={v} disabled={readOnly} onChange={(e) => setValue(f.name, e.target.value || null)}>
            <option value="" />
            {withCurrent(analysts.map((a) => a.name), v).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        );
      case 'AutoNumber':
        return <div id={id} className="dqc-in dqc-calc" title="Generated on save">{v || 'Autogenerated'}</div>;
      case 'Attachment':
        return (
          <div id={id} className="dqc-attach">
            {v && (entry && attachOid === entry.attachOid && entry.attachOid
              ? <button type="button" className="dqc-link" title="Download" onClick={() => void downloadAttachment(entry.oid, v)
                .catch((e) => notify.error(errorText(e, 'The attachment could not be downloaded.')))}>📎 {v}</button>
              : <span className="dqc-attach-name">📎 {v}</span>)}
            {!readOnly && (
              <>
                <label className={`ca-btn${uploading ? ' disabled' : ''}`}>
                  {uploading ? 'Uploading…' : v ? 'Replace' : 'Attach file'}
                  <input type="file" hidden disabled={uploading} onChange={(e) => { void attach(f, e.target.files?.[0]); e.target.value = ''; }} />
                </label>
                {v && (
                  <button type="button" className="ca-btn" title="Remove the attachment"
                    onClick={(e) => { markUserEdit(e.currentTarget); setAttachOid(null); setValue(f.name, null); }}>✕</button>
                )}
              </>
            )}
          </div>
        );
      default:
        return <input id={id} className={cls} type="text" maxLength={100} value={v} disabled={readOnly}
          onChange={(e) => setValue(f.name, e.target.value)} />;
    }
  };

  const passBadge = (f: DailyQcField) => {
    if (!f.accept) return null;
    const a = ev?.accept[f.name];
    return (
      <span className="dqc-pf">
        {a === true && <span className="adg-pill ok">Pass</span>}
        {a === false && <span className="adg-pill alert">Fail</span>}
      </span>
    );
  };

  const auditLine = entry
    ? `Entered by ${entry.enteredBy ?? ''} on ${fmtLocalDateTime(entry.dateEntered)}`
      + (entry.modifiedBy ? `. Last modified by ${entry.modifiedBy} on ${fmtLocalDateTime(entry.modifiedAt)}.` : '.')
      + (notCreator ? ' You can only edit rows created by you.' : '')
    : `Entered By and Date Entered are recorded automatically (${me?.name ?? ''}).`;

  const footer = readOnly
    ? <button className="ca-btn" onClick={cancel}>Close</button>
    : (
      <>
        <button className="ca-btn" onClick={cancel} title="Cancel (Esc)">Cancel</button>
        {!oid && (
          <button className="ca-btn" onClick={() => void doSave(true)} title={`Save & New (${formatCombo('mod+shift+s')})`}>
            Save &amp; New</button>
        )}
        <button className="ca-btn ca-btn-solid" onClick={() => void doSave(false)} title={`Save (${formatCombo('mod+s')})`}>
          Save</button>
      </>
    );

  return (
    <AppDialog
      open
      title={title}
      width={DIALOG_WIDTH}
      onClose={cancel}
      closeOnBackdrop={false}
      submitOnEnter={false}
      className="dqc-dlg"
      hotkeys={[{ combo: 'mod+shift+s', run: () => void doSave(true), enabled: !oid && !readOnly }]}
      footer={footer}
    >
      <div className="dqc-entry-top">
        <div>
          <b>{logbook?.name}</b>
          <div className="dqc-muted">
            {[[logbook?.instrumentCode, logbook?.instrumentName].filter(Boolean).join(' '), logbook?.method].filter(Boolean).join('  |  ')}
          </div>
        </div>
        <span className={`adg-pill ${STATUS_PILL[status]} dqc-status-lg`}>{status}</span>
      </div>
      {detail?.instructions && <div className="dqc-instructions">{detail.instructions}</div>}

      <div className="dqc-eform">
        {fields.map((f) => {
          const rule = ruleText(f);
          const required = f.required && !f.calc;
          return (
            <div className="dqc-efield" key={f.name}>
              <label htmlFor={`dqc_${f.name}`}>
                <span className="dqc-cap">{f.caption}{required && <span className="dqc-req"> *</span>}</span>
                {rule && <span className="dqc-rule" title={rule}>{rule}</span>}
              </label>
              <div className="dqc-ctrl">{control(f)}{passBadge(f)}</div>
              {f.type === 'Numeric' && sparkFor(f)}
            </div>
          );
        })}
      </div>

      <div className="dqc-eform dqc-eform2">
        <div className="dqc-efield dqc-span2">
          <label htmlFor="dqc_remark">
            <span className="dqc-cap">
              {failing ? `${remarkLabel} / Corrective Action` : remarkLabel}{failing && <span className="dqc-req"> *</span>}
            </span>
            {failing && <span className="dqc-rule dqc-fail">Required when a check fails</span>}
          </label>
          <textarea id="dqc_remark" ref={remarkRef} rows={2} maxLength={100} disabled={readOnly} value={remark}
            className={`dqc-in dqc-remark${showMissing && failing && !remark.trim() ? ' dqc-missing' : ''}`}
            onChange={(e) => setRemark(e.target.value)} />
        </div>
        <div className="dqc-efield">
          <label htmlFor="dqc_testedby"><span className="dqc-cap">{testedByLabel}</span></label>
          <select id="dqc_testedby" className="dqc-in" disabled={readOnly} value={testedBy}
            onChange={(e) => setTestedBy(e.target.value)}>
            <option value="" />
            {analysts.map((a) => <option key={a.oid} value={a.oid}>{a.name}</option>)}
          </select>
        </div>
      </div>
      <div className="dqc-note">{auditLine}</div>
    </AppDialog>
  );
}
